import { forwardRef, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import ClearIcon from '@tuhi/components/assets/icons/buttons/clear.svg'
import {
    textColorStyles,
    textSizeStyles,
    textWeightStyles,
} from '../text/textStyles'
import { BaseButton } from '../../components'
import { contentColorStyle } from '../buttons/button'
import { InputLabels, LabelProps } from './components/inputLabels'
import {
    InfoMessageProps,
    InputInfoMessage,
} from './components/inputInfoMessage'
import { media } from '../../theme/breakpoints'

type WidthType = 'medium' | 'fill'

const widthStyles = {
    medium: css`
        width: 320px;
    `,
    fill: css`
        width: 100%;
    `,
}

const Container = styled.div<{ $isError?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 8px;

    ${(p) => p.$isError && contentColorStyle(p.theme.colors.primaryRed)}
`

const InputContainer = styled.div<{
    $isActive?: boolean
    $width: WidthType
    $isError?: boolean
}>`
    display: flex;
    align-items: center;
    overflow: hidden;

    height: 36px;
    min-height: 36px;
    padding: 0px 16px;
    gap: 16px;

    ${(p) => widthStyles[p.$width]}

    background: ${(p) => p.theme.colors.white};
    border-radius: 8px;
    border: 1px solid
        ${(p) =>
            p.$isError
                ? p.theme.colors.primaryRed
                : p.theme.colors.primary.default};

    ${(p) =>
        contentColorStyle(
            p.$isError
                ? p.theme.colors.primaryRed
                : p.theme.colors.primary.default
        )}
`

const Input = styled.input`
    flex: 1;
    border: none;
    background: none;
    outline: none;

    ${textColorStyles['secondary']}
    ${textSizeStyles['xSmall']}
    ${textWeightStyles['regular']}

    ${media.mobile} {
        font-size: 16px;
    }

    &:focus {
        border: none;
        ${textColorStyles['primary']}
    }
`

interface Props {
    labels: LabelProps
    icon?: ReactNode
    value?: string
    placeholder?: string
    infoMessage?: InfoMessageProps
    width?: WidthType
    error?: boolean
    onChange: (text: string) => void
    onEnter?: () => void
}

export const InputField = forwardRef<HTMLInputElement, Props>((props, ref) => {
    const { placeholder, onChange, onEnter, value, width, error } = props

    const handleClear = () => {
        onChange('')
    }

    return (
        <Container $isError={error}>
            <InputLabels {...props.labels} />

            <InputContainer $width={width ?? 'medium'} $isError={error}>
                {props.icon}

                <Input
                    ref={ref}
                    value={value ?? ''}
                    type="text"
                    placeholder={placeholder}
                    onChange={(event) => {
                        onChange(event.target.value)
                    }}
                    onKeyDown={(e) => (e.key === 'Enter' ? onEnter?.() : '')}
                />

                {(value?.length ?? 0) > 0 && (
                    <BaseButton onClick={handleClear}>
                        <ClearIcon />
                    </BaseButton>
                )}
            </InputContainer>

            <InputInfoMessage {...props.infoMessage} />
        </Container>
    )
})
